<script setup>
import { HDivider, HIcon } from '@pixul/hephaestus';
import VersionAlert        from '@/components/VersionAlert/VersionAlert.vue';
import NavbarList          from '@/components/Navbar/NavbarList.vue';
import NotificationsModal  from '@/components/Notifications/NotificationsModal.vue';
import NavbarItem          from '@/components/Navbar/NavbarItem.vue';
import NavbarProfile       from '@/components/Navbar/NavbarProfile.vue';
import ReportBug           from '@/components/ReportBug/ReportBug.vue';
</script>

<template>
  <!-- Surrounded by div with the height of the actual navbar
  height so the navbar has an height even if it's in fixed position -->
  <div
    ref='navbar'
    class='navbar__container'
  >
    <HCol class='navbar'>
      <VersionAlert
        ref='version-alert'
      />
      <component
        :is='layoutComponent'
        v-click-outside='clickOutside'
        :align-items='alignItems'
        class='flex-1'
        :class="navbarDropdown ? 'navbar--mobile' : 'shadow--light'"
      >
        <HRow
          align-items='center'
          class='navbar__header padding-left-20'
          :class="{ 'padding-left-50' : isAdminMobile }"
          justify-content='space-between'
        >
          <HRow
            align-items='flex-end'
            gap='14'
          >
            <h4 class='text-blue-400'>
              Commissions
            </h4>
          </HRow>
          <HIcon
            v-if='navbarDropdown'
            class='navbar-header__icon padding-20'
            color='blue-400'
            :icon="opened ? 'xmark' : 'bars'"
            size='18'
            @click='toggleOpen'
          />
        </HRow>

        <component
          :is='layoutComponent'
          :align-items='alignItems'
          :class='listClasses'
          :gap='!navbarDropdown ? 14 : 0'
          justify-content='space-between'
        >
          <NavbarList
            :is-mobile='navbarDropdown'
            :items='navigationItems'
            @click-on-item='toggleOpen'
          />
          <template v-if='navbarDropdown'>
            <NavbarItem
              v-if='PermissionManager.getPermissionForElement(PermissionManager.permissionElement.dossiers.actions.create)'
              :is-mobile='navbarDropdown'
              :item='{ name : addFolderItem.name, path : addFolderItem.path }'
              @click='toggleOpen'
            />
            <HDivider v-if='userStore.hasAccess' />
            <NotificationsModal
              v-if='userStore.hasAccess'
              is-mobile
            />
            <NavbarList
              is-mobile
              :items='profileItems'
              @click-on-item='toggleOpen'
            />
          </template>
          <HRow
            v-else
            align-items='center'
            gap='14'
          >
            <NavbarItem
              v-if='PermissionManager.getPermissionForElement(PermissionManager.permissionElement.dossiers.actions.create)'
              :is-mobile='navbarDropdown'
              :item='addFolderItem'
              only-icon
              :tooltip="$t('menu.folders-create')"
            />
            <NotificationsModal
              v-if='userStore.hasAccess'
              :is-mobile='navbarDropdown'
            />
            <NavbarProfile :items='profileItems' />
          </HRow>
        </component>
      </component>
    </HCol>
    <ReportBug
      v-if='reportBugModal'
      @close='reportBugModal = false'
    />
  </div>
</template>

<script>
import { mapStores }         from 'pinia';
import { useUserStore }      from '@/store/user';
import { useQueryStore }     from '@/store/query';
import { HRow, HCol }        from '@pixul/hephaestus';
import Config                from '@/config';
import { PermissionManager } from '@/utils/permissions/permissionManager';

export default {
  name : 'Navbar',

  components : {
    HRow,
    HCol
  },

  data() {

    return {
      opened          : false,
      reportBugModal  : false,
      navigationItems : []
    };
  },

  computed : {
    ...mapStores(useUserStore, useQueryStore),

    isAdminMobile() {

      return this.isMaxSmall && this.$route.name.includes('admin');
    },

    navbarDropdown() {

      return this.isMaxXLarge;
    },

    profileItems() {

      const isSuperAdmin = PermissionManager.getPermissionForElement(PermissionManager.permissionElement.admin.show);

      return [
        { name : 'profile', path : this.getProfilePath(), icon : { name : 'arrow-up-right-from-square' } },
        { name : 'language' },
        { name : 'admin', path : this.getPath('admin'), function : () => this.queryStore.resetAllQueries(), icon : { name : 'gear' }, hide : !isSuperAdmin },
        { name : 'report-bug', function : () => this.reportBugModal = true, icon : { name : 'exclamation-triangle' } },
        {
          name : 'logout',
          path : this.getPath('logout'),
          icon : { name : 'arrow-right-from-bracket', type : 'solid', color : 'red' }
        }
      ];
    },

    addFolderItem() {

      return {
        name : 'folders-create',
        path : this.getPath('folders-create'),
        icon : { name : 'folder-plus', type : 'solid', color : 'grey-600' }
      };
    },

    layoutComponent() {

      if (this.navbarDropdown) {

        return 'HCol';
      }

      return 'HRow';
    },

    showItems() {

      return this.opened || !this.navbarDropdown;
    },

    alignItems() {

      return this.navbarDropdown ? 'initial' : 'center';
    },

    listClasses() {

      const classes = ['navbar__list padding-left-30 padding-right-30'];

      if (this.navbarDropdown) {

        classes.push('shadow--light');
      }

      if (this.showItems) {

        classes.push('navbar__list--opened');
      }

      return classes;
    }
  },

  watch : {
    'userStore.hasAccess'() {

      this.setNavigationItems();
    }
  },

  created() {

    this.setNavigationItems();
  },

  mounted() {

    const versionAlert = this.$refs['version-alert'];
    const hasAlert = !!versionAlert.$el.tagName;

    if (hasAlert) {

      const navbar = this.$refs['navbar'];
      navbar.style.setProperty('--navbar-height', `${ 50 + 41 }px`);
    }
  },

  methods : {
    setNavigationItems() {

      if (!this.userStore.hasAccess) {

        this.navigationItems = [];
        return;
      }

      const canAccessProformas = PermissionManager.getPermissionForElement(PermissionManager.permissionElement.proforma.show);
      const canAccessProformasToInvoice = PermissionManager.getPermissionForElement(PermissionManager.permissionElement.proforma.showProformasToInvoice);
      const canAccessOffers = PermissionManager.getPermissionForElement(PermissionManager.permissionElement.offer.show);
      const canAccessInvoices = PermissionManager.getPermissionForElement(PermissionManager.permissionElement.invoice.show);
      const canAccessThirdPartyInvoicesToBeCreated = PermissionManager.getPermissionForElement(PermissionManager.permissionElement.thirdPartyInvoice.showInvoicesToBeCreated);

      this.navigationItems = [
        { name : 'my-business', path : this.getPath('my-business') },
        { name : 'classeurs-list', path : this.getPath('classeurs-list'), function : () => this.queryStore.resetAllQueries() },
        { name : 'folders-list', path : this.getPath('folders-list'), function : () => this.queryStore.resetAllQueries() },
        {
          name     : 'notaries-clerks-list',
          children : [
            { name : 'notaries-list', path : this.getPath('notaries-list'), function : () => this.queryStore.resetAllQueries() },
            { name : 'clerks-list', path : this.getPath('clerks-list'), function : () => this.queryStore.resetAllQueries() }
          ]
        },
        { name : 'tasks-list', function : () => this.queryStore.resetAllQueries() },
        {
          name     : 'proformas-list',
          children : [
            { name : 'proformas-list', path : this.getPath('proformas-list'), function : () => this.queryStore.resetAllQueries() },
            { name : 'folders-to-calculate', path : this.getPath('folders-to-calculate'), function : () => this.queryStore.resetAllQueries(), hide : !canAccessProformasToInvoice }
          ],
          hide     : !canAccessProformas
        },
        {
          name     : 'invoices-list',
          children : [
            { name : 'issued-invoices-list', path : this.getPath('issued-invoices-list'), function : () => this.queryStore.resetAllQueries() },
            { name : 'incoming-invoices-list', path : this.getPath('incoming-invoices-list'), function : () => this.queryStore.resetAllQueries() },
            { name : 'third-party-invoices-to-be-created-list', path : this.getPath('third-party-invoices-to-be-created-list'), function : () => this.queryStore.resetAllQueries(), hide : !canAccessThirdPartyInvoicesToBeCreated }
          ],
          hide     : !canAccessInvoices
        },
        {
          name     : 'offers-bookings',
          children : [
            { name : 'offers-list', path : this.getPath('offers-list'), function : () => this.queryStore.resetAllQueries() },
            { name : 'bookings-list', path : this.getPath('bookings-list'), function : () => this.queryStore.resetAllQueries() }
          ],
          hide     : !canAccessOffers
        }
      ];
    },

    getProfilePath() {

      return `${ Config.hestiaURL }/tenant/${ this.userStore.user.tenantId }/users/${ this.userStore.user.id }/details`;
    },

    getPath(name) {

      return this.$router.resolve({
        name   : name,
        params : {
          tenant : this.userStore.tenant
        }
      }).href;
    },

    toggleOpen() {

      this.opened = !this.opened;
    },

    clickOutside() {

      this.opened = false;
    }
  }
};
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
.navbar__container {
  --navbar-height: 50px;
  height: var(--navbar-height);
}

.navbar {
  position: fixed;
  width: 100vw;
  z-index: 90;
  min-height: 0;
  height: var(--navbar-height);
}

.navbar__header {
  position: relative;
  height: 100%;
  background: var(--white);
  z-index: 2;
}

.navbar-header__icon {
  cursor: pointer;
}

.navbar__list {
  position: relative;
  background: var(--white);
  z-index: 1;
  width: 100%;
  height: 100%;
}

.navbar--mobile {
  height: 0;

  .navbar__header {
    border-bottom: 1px solid var(--grey-300);
  }

  .navbar__list {
    width: initial;
    height: initial;
    transform: translateY(-100%);
    transition: .3s transform ease-in;
    padding: 7px 14px;
  }

  .navbar__list--opened {
    transform: translateY(0);
  }
}
</style>
